/**
 * client/src/components/NarrateBook.tsx — the button that spends a fortnight of GPU, and the sentence
 * that has to be next to it.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️ THE PRICE IS THE FEATURE. A BUTTON THAT STARTS THIRTEEN NIGHTS OF GPU WITHOUT SAYING SO IS THE
 * WRONG BUTTON.
 *
 * PLAN-P7 §4.3, priced against the real shelf at `GPU_CHARS_PER_SEC = 56` and
 * `AB_AUTO_NARRATE_GPU_MIN_PER_DAY = 90`:
 *
 *   Alice ~43 GPU-min → **1 night** · Sherlock ~2.8 h → **2** · Sai Satcharitra ~4.5 h → **3** ·
 *   **the KJV Bible ~19.4 h → ~13 nights**
 *
 * §4.9's *"a full 12-hour book is ~3.3 GPU-hours"* is a typical novel. The book Karti named first is
 * six times that, and "about 19 hours of GPU" reads like a long afternoon to anybody who is not
 * holding the ceiling in their head. So this screen renders **nights**, computed on the server
 * (`sys/sweep.ts`'s `nightsLabel`) so that two surfaces cannot disagree about a price.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ── The three things it says before anything is spent ───────────────────────
 *
 *  1. **What it costs**, in pages, in GPU, in hours of narration and in bytes — the same
 *     `PreparationQuote` the room lobby and the chapter button already render, so the numbers on
 *     three screens come from one walk.
 *  2. **What today buys.** The ceiling is real: production had spent 79.9 of 90 unattended GPU-minutes
 *     by mid-morning on 2026-08-08. `today.pages` is what a single priced pass would actually start,
 *     and showing only the whole-book figure would be a promise the box cannot keep tonight.
 *  3. **Whether the disk will let it.** `sys/sweep.ts` refuses at 80 %; production sat at 71.4 % while
 *     a Bible sweep is ~1.6 GB of delivery audio. A refusal met *after* the tap is a bug report; the
 *     same refusal beside the button is the feature.
 *
 * ── Two buttons, because they are two different promises ────────────────────
 *
 * *Narrate what today's budget allows* is the room lobby's priced preparation aimed at a book:
 * priority 50, one pass, over when the ceiling is. **Any owner may press it** — AGENTS.md's
 * escape-hatch table, row three.
 *
 * *Sweep it, night after night* writes one `book_sweeps` row and returns; a timer ticks it at
 * priority 100 for as many nights as it takes, across restarts. **God only** — the same table, row
 * four — and it asks a second time before it starts, because §10 spells this route *"requires
 * `--confirm`"* and thirteen nights is the one number a person should have to look at twice.
 *
 * ── Why it says 7.7 seconds and not 4 ───────────────────────────────────────
 *
 * The honest worry about a background sweep is *"will it be in my way?"*. `PRIORITY.sweep = 100` plus
 * the render loop's yield means an interactive `ensure` overtakes it within one clip. §4.8 computes
 * that as ~1.4–4 s; `docs/tts-measurements.md:705` **measured 7.7 s** against a spark-1 sharing its
 * card with four other inference services. The server sends its own constant and this file renders it
 * — quoting the optimistic figure to somebody who then waits eight seconds is how a true statement
 * becomes a broken promise.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { formatBytes } from "../App.tsx";
import { ApiError, api } from "../lib/api.ts";

// ─── the wire, read defensively ──────────────────────────────────────────────

interface Quote {
  pages: number;
  pagesReady: number;
  pagesNeedingGpu: number;
  pagesNeedingStitch: number;
  pagesSkippedNotBody: number;
  estGpuSec: number;
  estAudioSec: number;
  estBytes: number;
  label: string;
  audioLabel: string;
  withinBudget: boolean;
  pagesAffordable: number;
  refused: { code: string; message: string } | null;
}

interface SweepStatus {
  bookId: string;
  state: string;
  message: string;
  cursorOrd: number;
  gpuSecSpent: number;
  pagesBody: number;
  pagesPassed: number;
  fraction: number;
  queuedPages: number;
  startedAt: number;
  updatedAt: number;
  blocked: { state: string; message: string } | null;
}

interface NarrateQuote {
  quote: Quote;
  today: { pages: number; withinBudget: boolean; label: string };
  whole: {
    pages: number;
    estGpuSec: number;
    gpuLabel: string;
    audioLabel: string;
    estBytes: number;
    nights: number;
    nightsLabel: string;
    gpuMinPerDay: number;
  };
  disk: { usedPercent: number | null; stopPercent: number; blocked: boolean };
  coverage: { pagesTotal: number; pagesAudible: number; pagesWithAudio: number } | null;
  sweep: SweepStatus | null;
  may: { prepare: boolean; sweep: boolean };
  overtakeSec: number;
  tickSec: number;
}

/**
 * How often the screen re-asks while a sweep is live.
 *
 * Twenty seconds, and only while the tab is visible. The sweep itself ticks once a minute, so a
 * faster poll would mostly re-render the same row; a slower one would leave *"queued 1 page"* on
 * screen long enough to look stuck. `document.hidden` is checked on every tick rather than relying on
 * the browser to throttle: a backgrounded tab polling cloud-2 forever is a connection the box holds
 * for nothing, and this product is mainly used on a phone.
 */
const POLL_MS = 20_000;

/** The sweep states that are still going somewhere, and therefore still worth polling. */
const LIVE_STATES = new Set(["running", "waiting-for-budget", "waiting-for-disk", "waiting-for-worker", "drained"]);

export interface NarrateBookProps {
  bookId: string;
  /** The book's title, for the confirmation. A fortnight of GPU should name what it is spent on. */
  title: string;
}

export function NarrateBook({ bookId, title }: NarrateBookProps): React.ReactElement | null {
  const [data, setData] = useState<NarrateQuote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"prepare" | "sweep" | "stop" | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  // The last load wins. A `Stop` that resolves after a background poll would otherwise be painted
  // over by the poll's older answer, and the row would come back to life on screen while being dead
  // on the server — which is the shape of bug that makes people press a button twice.
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const body = await api.get<NarrateQuote>(`/books/${encodeURIComponent(bookId)}/narrate`);
      if (mine !== seq.current) return;
      setData(body);
      setError(null);
    } catch (err) {
      if (mine !== seq.current) return;
      // A 404 here is LAW 17 doing its job — this component is only rendered for an owner, so it
      // means the book went away underneath us. Nothing to render, and nothing alarming to say.
      if (err instanceof ApiError && err.notFound) {
        setData(null);
        return;
      }
      setError(err instanceof ApiError ? err.message : "Couldn't price this book.");
    }
  }, [bookId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sweepState = data?.sweep?.state ?? null;
  const live = sweepState !== null && LIVE_STATES.has(sweepState);
  useEffect(() => {
    if (!live) return;
    const id = window.setInterval(() => {
      if (document.hidden) return;
      void load();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [live, load]);

  const act = useCallback(
    async (kind: "prepare" | "sweep" | "stop") => {
      setBusy(kind);
      setError(null);
      setResult(null);
      try {
        const body =
          kind === "stop"
            ? await api.del<{ message?: string }>(`/books/${encodeURIComponent(bookId)}/narrate`)
            : await api.post<{ message?: string }>(`/books/${encodeURIComponent(bookId)}/narrate`, {
                whole: kind === "sweep",
              });
        setResult(typeof body?.message === "string" ? body.message : null);
        setConfirming(false);
      } catch (err) {
        // The server's own sentence, always. Every refusal on this route names what it refused and
        // what would fix it — `unconfirmed`, `sweep-forbidden`, the quota's "whose problem it is" —
        // and replacing them with "Something went wrong" is how a person re-presses the same button.
        setError(err instanceof ApiError ? err.message : "Couldn't start it.");
      } finally {
        setBusy(null);
        await load();
      }
    },
    [bookId, load],
  );

  if (data === null) {
    if (error === null) return null;
    return (
      <section className="ab-panel">
        <h2 className="ab-h2">Narrate the whole book</h2>
        <p className="ab-hint ab-hint-bad">{error}</p>
      </section>
    );
  }

  const { quote, whole, today, disk, sweep } = data;

  if (quote.refused !== null) {
    return (
      <section className="ab-panel">
        <h2 className="ab-h2">Narrate the whole book</h2>
        {/* The refusal verbatim. `unconfirmed` is the one that will actually happen, and its own text
            already says which screen fixes it, so re-writing it here would only make it vaguer. */}
        <p className="ab-hint">{quote.refused.message}</p>
      </section>
    );
  }

  const nothingLeft = whole.pages === 0;

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">Narrate the whole book</h2>

      {sweep !== null && <SweepRow sweep={sweep} tickSec={data.tickSec} busy={busy === "stop"} onStop={() => void act("stop")} />}

      {nothingLeft ? (
        <p className="ab-lede">
          Every body page of this book already has a voice
          {data.coverage !== null && ` — ${data.coverage.pagesWithAudio} of ${data.coverage.pagesAudible}`}. There is
          nothing left to generate.
        </p>
      ) : (
        <>
          <p className="ab-lede">
            {whole.pages} page{whole.pages === 1 ? "" : "s"} still need a voice — <strong>{whole.gpuLabel}</strong>,{" "}
            {whole.audioLabel}, about {formatBytes(whole.estBytes)} on disk.
          </p>
          <p className="ab-hint">
            {/* The number that turns an abstract price into a plan. */}
            That is <strong>{whole.nightsLabel}</strong>.{" "}
            {quote.pagesNeedingStitch > 0 &&
              `${quote.pagesNeedingStitch} of them need no GPU at all — they are half a second of CPU each. `}
            {quote.pagesSkippedNotBody > 0 &&
              `${quote.pagesSkippedNotBody} page${quote.pagesSkippedNotBody === 1 ? " is" : "s are"} front or back matter and ${quote.pagesSkippedNotBody === 1 ? "is" : "are"} left out.`}
          </p>
          <p className="ab-hint ab-hint-quiet">
            The text is already imported. This estimate is the work to generate Karti&rsquo;s voice;
            finishing pages can take longer while their audio is stitched and copied into the reader.
          </p>

          {disk.usedPercent !== null && (
            <p className={disk.blocked ? "ab-hint ab-hint-bad" : "ab-hint ab-hint-quiet"}>
              {/* Shown always, not only when it blocks: the whole point of a disk number on this
                  screen is that somebody sees it climbing before it stops anything. */}
              Disk is {disk.usedPercent}% full; a sweep stops at {disk.stopPercent}%.
            </p>
          )}

          {!today.withinBudget && (
            <p className="ab-hint ab-hint-quiet">
              Today&rsquo;s remaining unattended budget can start up to {today.pages} of them; those pages
              are not ready until generation and stitching finish. The rest waits for tomorrow — nothing
              is lost, and pages you open are narrated on demand either way.
            </p>
          )}
        </>
      )}

      {error !== null && <p className="ab-hint ab-hint-bad">{error}</p>}
      {result !== null && <p className="ab-hint">{result}</p>}

      {!nothingLeft && (
        <div className="ab-actions">
          <button
            type="button"
            className="ab-btn ab-btn-primary"
            disabled={busy !== null || disk.blocked}
            onClick={() => void act("prepare")}
          >
            {busy === "prepare"
              ? "Starting…"
              : today.pages >= whole.pages
                ? `Narrate it — ${whole.gpuLabel}`
                : `Narrate ${today.pages} page${today.pages === 1 ? "" : "s"} now`}
          </button>

          {data.may.sweep && sweep === null && !confirming && (
            <button type="button" className="ab-btn ab-btn-ghost ab-btn-wrap" disabled={busy !== null} onClick={() => setConfirming(true)}>
              Sweep it, night after night
            </button>
          )}
        </div>
      )}

      {confirming && (
        <div className="ab-confirm-row">
          <div className="ab-confirm-text">
            <strong>
              Sweep {title} — {whole.nightsLabel}?
            </strong>
            <p className="ab-hint">
              One page at a time at the lowest priority, resuming by itself after a restart and after
              each night&rsquo;s ceiling. Anything you open overtakes it within about {data.overtakeSec} seconds.
              It stops on its own if the disk reaches {disk.stopPercent}%, and you can stop it here at any time.
            </p>
          </div>
          <div className="ab-actions">
            <button type="button" className="ab-btn ab-btn-primary ab-btn-sm" disabled={busy !== null} onClick={() => void act("sweep")}>
              {busy === "sweep" ? "Starting…" : `Start the sweep — ${whole.nightsLabel}`}
            </button>
            <button type="button" className="ab-btn ab-btn-ghost ab-btn-sm" disabled={busy !== null} onClick={() => setConfirming(false)}>
              Never mind
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * The live sweep, and the one thing it must never do: look busy while it is stuck.
 *
 * `state` is the last tick's verdict and `message` is re-derived on the server from live facts every
 * time this is asked — so *"waiting for tomorrow's GPU"* becomes *"narrating"* the moment the ceiling
 * rolls, with nothing to invalidate. See `server/src/sys/sweep.ts`'s header for why there is no stored
 * reason string.
 */
function SweepRow({
  sweep,
  tickSec,
  busy,
  onStop,
}: {
  sweep: SweepStatus;
  tickSec: number;
  busy: boolean;
  onStop: () => void;
}): React.ReactElement {
  const done = sweep.state === "done";
  const stopped = sweep.state === "stopped";
  const stuck = sweep.blocked !== null;
  const percent = Math.round(sweep.fraction * 100);

  return (
    <div className="ab-confirm-row">
      <div className="ab-confirm-text">
        <strong>
          {done ? "Swept" : stopped ? "Sweep stopped" : stuck ? "Sweep paused" : "Sweeping"}
          {" · "}
          {sweep.pagesPassed} of {sweep.pagesBody} body pages
          {sweep.pagesBody > 0 && ` · ${percent}%`}
        </strong>
        <p className={stuck ? "ab-hint ab-hint-bad" : "ab-hint"}>{sweep.message}</p>
        {!done && !stopped && !stuck && (
          <p className="ab-hint ab-hint-quiet">
            {/* LAW 9's habit in copy: a queue depth of zero between ticks is not "nothing is
                happening", it is the sweep behaving exactly as designed, and saying so stops
                somebody pressing the button again. */}
            {sweep.queuedPages > 0
              ? `${sweep.queuedPages} page${sweep.queuedPages === 1 ? "" : "s"} in the queue right now.`
              : `Nothing queued at this instant — it offers the next page every ${tickSec} seconds.`}
          </p>
        )}
        <p className="ab-hint ab-hint-quiet">
          About {Math.round(sweep.gpuSecSpent / 60)} minutes of GPU committed by this sweep so far.
        </p>
      </div>
      {!done && !stopped && (
        <div className="ab-actions">
          <button type="button" className="ab-btn ab-btn-ghost ab-btn-sm" disabled={busy} onClick={onStop}>
            {busy ? "Stopping…" : "Stop the sweep"}
          </button>
        </div>
      )}
    </div>
  );
}
